import React from 'react';
import { Shield, Star, Building2 } from 'lucide-react';

interface VerificationBannerProps {
  verificationStatus: {
    id?: { status: string };
    address?: { status: string };
  } | undefined;
  onStartVerification: (type: 'id' | 'address') => void;
}

const VerificationBanner: React.FC<VerificationBannerProps> = ({
  verificationStatus,
  onStartVerification
}) => {
  const idVerified = verificationStatus?.id?.status === 'verified';
  const addressVerified = verificationStatus?.address?.status === 'verified';
  const idPending = verificationStatus?.id?.status === 'pending';
  const addressPending = verificationStatus?.address?.status === 'pending';

  const getVerificationStatus = () => {
    if (idVerified && addressVerified) return 'host';
    if (idVerified) return 'basic';
    return 'none';
  };

  const status = getVerificationStatus();

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
      {/* Status Header */}
      <div className="p-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {status === 'host' ? (
              <>
                <Star className="h-5 w-5" />
                <span className="font-medium">Host Verified</span>
              </>
            ) : status === 'basic' ? (
              <>
                <Shield className="h-5 w-5" />
                <span className="font-medium">Basic Verified</span>
              </>
            ) : (
              <>
                <Shield className="h-5 w-5" />
                <span className="font-medium">Verification Required</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Verification Steps */}
      <div className="p-4 space-y-4">
        {/* ID Verification */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Shield className={`h-5 w-5 ${idVerified ? 'text-green-500' : 'text-gray-400'}`} />
            <div>
              <h3 className="font-medium text-gray-900">Identity Verification</h3>
              <p className="text-sm text-gray-500">
                {idVerified ? 'Identity verified' : 
                 idPending ? 'Verification pending review' :
                 'Required for all users'}
              </p>
            </div>
          </div>
          {!idVerified && !idPending && (
            <button
              onClick={() => onStartVerification('id')}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Verify ID
            </button>
          )}
          {idPending && (
            <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm">
              Pending Review
            </span>
          )}
        </div>

        {/* Address Verification */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Building2 className={`h-5 w-5 ${addressVerified ? 'text-green-500' : 'text-gray-400'}`} />
            <div>
              <h3 className="font-medium text-gray-900">Address Verification</h3>
              <p className="text-sm text-gray-500">
                {addressVerified ? 'Address verified' :
                 addressPending ? 'Verification pending review' :
                 'Required to become a host'}
              </p>
            </div>
          </div>
          {!addressVerified && !addressPending && (
            <button
              onClick={() => onStartVerification('address')}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Verify Address
            </button>
          )}
          {addressPending && (
            <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm">
              Pending Review
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerificationBanner;